// ABOUTME: Catalog view for prototype selection using the ModelSelection component.
// ABOUTME: Wraps ModelSelectionProvider with data from useModelSelectionData and handles product click.

import type {
    ICategory,
    IFilter,
    IModelSelectionContext,
    IModelSelectionData,
    IModelSelectionSettings,
    IMSHooks,
    IProduct,
} from '@coolshop/cool-model-selection';
import {
    CompareProductsProvider,
    ModelSelection,
    ModelSelectionProvider,
    useModelSelection,
} from '@coolshop/cool-model-selection';
import { BoxUnstable } from '@ds/coolshop';
import { usePimSidebarOpen } from '@src/_new/hooks/usePimSidebarOpen';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { MouseEvent, MutableRefObject, useContext, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { OPTION_FILTER_CODE_PREFIX, RangeSelection } from '../../hooks/useModelSelectionData';
import { ActiveCatalogFilters } from '../../hooks/useResolveFilterAssignments';
import { CatalogFamilyCardContent } from './CatalogFamilyCardContent';
import { CatalogFavoritesProvider } from './CatalogFavoritesContext';
import { CatalogProductRowContent } from './CatalogProductRowContent';
import { CatalogRootHeader } from './CatalogRootHeader';
import { ProductCardContent, ProductCardContentProps } from './ProductCardContent';

interface CatalogViewProps {
    data: IModelSelectionData;
    /** Also receives the sidebar filters active at the click, so the configurator can open on them. */
    onProductClick: (product: IProduct, activeFilters: ActiveCatalogFilters) => void;
    loadSubranges: (params: { range: ICategory }) => Promise<ICategory[] | undefined>;
    loadSearchResults: (params: { searchText: string }) => Promise<ICategory[] | undefined>;
    loadFilters?: (params: {
        categoryId?: string;
        selectedFilters?: Record<string, string[]>;
        selectedRanges?: Record<string, RangeSelection>;
    }) => Promise<IFilter[] | undefined>;
    onFiltersChange?: (
        filters: Record<string, string[]>,
        parentCategoryId?: string,
        ranges?: Record<string, RangeSelection>,
    ) => Promise<ICategory[] | undefined>;
    /** Shows the favorite star on every product card. Off by default: the add-to-offer and
     * add-to-cart dialogs pick a product for the current flow rather than bookmark it. */
    enableFavoriteAction?: boolean;
    /** Title of the `treeview` sidebar; "Catalog" when none. The default layout shows none. */
    title?: string;
    /** Shows the back button above the `treeview` sidebar title; the default layout shows none. */
    onBack?: () => void;
    /** Label of that back button; the package's "Back" when none. */
    backLabel?: string;
}

// Both variants are module-level so the `OverrideContent` identity stays stable across renders: an
// inline arrow would be a new component type each time and remount every card.
const CardContentWithFavorite = (props: ProductCardContentProps) => (
    <ProductCardContent {...props} showFavorite />
);

const CardContent = (props: ProductCardContentProps) => <ProductCardContent {...props} />;

// The treeview catalog lays the models out as rows (image, name, price, actions), two per line.
const RowContentWithFavorite = (props: ProductCardContentProps) => (
    <CatalogProductRowContent {...props} showFavorite />
);

const RowContent = (props: ProductCardContentProps) => <CatalogProductRowContent {...props} />;

// Stable identity for the same reason as the product card contents above.
const TREEVIEW_FAMILY_CARD_PROPS = { OverrideContent: CatalogFamilyCardContent };

// The package lays the product cards out in `Grid item xs={6}` over 12 columns — two per row,
// whatever the width — while the category cards use `columns={{ xs: 4, sm: 12, md: 16, lg: 20 }}`
// with `item xs={4}`, i.e. 1/3/4/5 per row. Since the model cards now carry an image like the
// category ones, they are re-widthed to those same columns so both levels read as one grid.
// Scoped to the grid items holding a product card, so the category grid is untouched.
// `BoxUnstable` is the design-system Box that forwards `sx`; the plain `Box` drops it.
const PRODUCT_GRID_COLUMNS = { xs: '100%', sm: '33.333%', md: '25%', lg: '20%' };
const PRODUCT_GRID_SX = {
    // The wrapper sits between the host's fixed-height box and the package layout: without a height
    // the layout's `height: 100%` resolves to auto, it grows with its content and the whole host
    // (e.g. the selection dialog) scrolls instead of the filter sidebar and the card area alone.
    height: '100%',
    '.MuiGrid-item:has([data-testid="product-card"])': {
        flexBasis: PRODUCT_GRID_COLUMNS,
        maxWidth: PRODUCT_GRID_COLUMNS,
        // The grid row already stretches its cells; the card has to fill its cell too, or a model
        // whose name wraps to more lines makes its card stick out below the others in the row.
        '> [data-testid="product-card"]': { height: '100%' },
    },
};

// The treeview catalog: the model rows two per line, the family cards six per line on a wide screen.
// Scoped like PRODUCT_GRID_SX, to the grid items holding the respective cards.
const TREEVIEW_PRODUCT_COLUMNS = { xs: '100%', md: '50%' };
const TREEVIEW_FAMILY_COLUMNS = { xs: '50%', sm: '33.333%', md: '25%', lg: '16.666%' };
const TREEVIEW_GRID_SX = {
    height: '100%',
    '.MuiGrid-item:has([data-testid="product-card"])': {
        flexBasis: TREEVIEW_PRODUCT_COLUMNS,
        maxWidth: TREEVIEW_PRODUCT_COLUMNS,
        '> [data-testid="product-card"]': { height: '100%' },
    },
    '.MuiGrid-item:has([data-testid="catalog-family-card"])': {
        flexBasis: TREEVIEW_FAMILY_COLUMNS,
        maxWidth: TREEVIEW_FAMILY_COLUMNS,
    },
};

// `enableFilters` is decided per tenant (see CatalogView): the catalog filter sidebar and the
// /filters call exist only where the tenant has content-type filters or the option filters on.
const MODEL_SELECTION_SETTINGS: Omit<IModelSelectionSettings, 'enableFilters'> = {
    enableKeyFeatures: false,
    enableSubRanges: true,
    expandSubRanges: false,
    enableTechData: false,
    enablePrice: false,
    enableGroupAutoSkip: false,
    enableSkipToSubRange: false,
    hideSearchBar: false,
    // The filtered refetch replaces the categories under the same document: the families and the
    // browsed category must follow it, or a pick that empties a family keeps its products on screen.
    syncFilteredCategories: true,
};

function ActionsCapture({
    actionsRef,
    filtersRef,
}: {
    actionsRef: MutableRefObject<IModelSelectionContext['actions'] | null>;
    filtersRef: MutableRefObject<ActiveCatalogFilters>;
}) {
    const { actions, selectedFilters, selectedRanges, group, gamma, range } = useModelSelection();
    const categoryId = (range || gamma || group)?.id;
    useEffect(() => {
        // eslint-disable-next-line no-param-reassign
        actionsRef.current = actions;
    }, [actionsRef, actions]);
    // Read from the context rather than from onFiltersChange, which the package does not call
    // when the filters are cleared.
    useEffect(() => {
        // eslint-disable-next-line no-param-reassign
        filtersRef.current = { filters: selectedFilters, ranges: selectedRanges, categoryId };
    }, [filtersRef, selectedFilters, selectedRanges, categoryId]);
    return null;
}

/**
 * Option facets exist only inside a product family, so the server returns none at the catalog
 * root. A pick made inside a family would otherwise survive the way back up, filtering the whole
 * catalog with no sidebar row left to undo it: this drops the option picks when the user returns
 * to the root and leaves the content-type picks alone.
 */
function RootOptionSelectionReset() {
    const { group, gamma, range, selectedFilters, selectedRanges, actions } = useModelSelection();
    useEffect(() => {
        if (group || gamma || range) {
            return;
        }
        const optionEntries = Object.keys(selectedFilters).filter(code =>
            code.startsWith(OPTION_FILTER_CODE_PREFIX),
        );
        const rangeEntries = Object.keys(selectedRanges);
        if (!optionEntries.length && !rangeEntries.length) {
            return;
        }
        if (optionEntries.length) {
            actions.setSelectedFilters(
                Object.fromEntries(
                    Object.entries(selectedFilters).filter(
                        ([code]) => !code.startsWith(OPTION_FILTER_CODE_PREFIX),
                    ),
                ),
            );
        }
        if (rangeEntries.length) {
            actions.setSelectedRanges({});
        }
    }, [group, gamma, range, selectedFilters, selectedRanges, actions]);
    return null;
}

export function CatalogView({
    data,
    onProductClick,
    loadSubranges,
    loadSearchResults,
    loadFilters,
    onFiltersChange,
    enableFavoriteAction = false,
    title,
    onBack,
    backLabel,
}: CatalogViewProps) {
    const { formatMessage } = useIntl();
    const actionsRef = useRef<IModelSelectionContext['actions'] | null>(null);
    const filtersRef = useRef<ActiveCatalogFilters>({ filters: {}, ranges: {} });
    const tenantSettings = useContext(SettingsContext);
    // The sidebar serves two kinds of facets: the content-type ones (on wherever the tenant set a
    // `filterContentTypeGroup`) and the option ones (on with `optionFilters.enable`; the server adds
    // them only then). With neither the package skips loadFilters, and the callbacks are not even
    // handed over, so no /filters request can leave the catalog.
    const filtersEnabled =
        !!tenantSettings.prototype?.filterContentTypeGroup ||
        !!tenantSettings.prototype?.optionFilters?.enable;
    // `treeview` lays the catalog out as the configurator does: the filters in a left panel.
    const layout =
        tenantSettings.prototype?.modelSelectionLayout === 'treeview' ? 'treeview' : 'default';
    // Sized like the configurator's sidebar: the package default unless the tenant set a width, a
    // blank one reading as unset.
    const sidebarWidth = tenantSettings.prototype?.modelSelectionSidebarWidth?.trim() || undefined;
    // The filters make room for the cart docked open beside the page, as every PIM sidebar does.
    const sidebarOpen = usePimSidebarOpen();
    const sidebarTitle =
        title ?? formatMessage({ id: 'cpq.catalog.title', defaultMessage: 'Catalog' });
    const settings: IModelSelectionSettings = useMemo(
        () => ({
            ...MODEL_SELECTION_SETTINGS,
            enableFilters: filtersEnabled,
            layout,
            // The treeview lists a step's models flat, as its visuals: no accordion per subrange.
            ...(layout === 'treeview' ? { enableSubRanges: false } : {}),
        }),
        [filtersEnabled, layout],
    );

    const handleClick = (_event: MouseEvent<HTMLDivElement>, product: IProduct) => {
        onProductClick(product, filtersRef.current);
    };
    const isTreeview = layout === 'treeview';

    const hooks: IMSHooks = useMemo(
        () => ({
            onGroupClick: ({ group }) => {
                actionsRef.current?.setGroup(group);
                actionsRef.current?.setSearchTags([group]);
            },
            onGammaClick: ({ group, gamma }) => {
                if (gamma) {
                    actionsRef.current?.setGamma(gamma);
                    actionsRef.current?.setSearchTags(
                        [group, gamma].filter((tag): tag is ICategory => Boolean(tag)),
                    );
                }
            },
            onRangeClick: ({ group, gamma, range }) => {
                if (range) {
                    actionsRef.current?.setRange(range);
                    actionsRef.current?.setSearchTags(
                        [group, gamma, range].filter((tag): tag is ICategory => Boolean(tag)),
                    );
                }
            },
        }),
        [],
    );

    return (
        <ModelSelectionProvider
            data={data}
            settings={settings}
            hooks={hooks}
            loadSubranges={loadSubranges}
            loadSearchResults={loadSearchResults}
            loadFilters={filtersEnabled ? loadFilters : undefined}
            onFiltersChange={filtersEnabled ? onFiltersChange : undefined}
        >
            <ActionsCapture actionsRef={actionsRef} filtersRef={filtersRef} />
            <RootOptionSelectionReset />
            <CompareProductsProvider currency="">
                {isTreeview ? (
                    // The treeview catalog: family cards with their star, the favorites above
                    // them at the root, and the models as rows.
                    <CatalogFavoritesProvider>
                        <BoxUnstable sx={TREEVIEW_GRID_SX}>
                            <ModelSelection
                                treeviewProps={{
                                    sidebarTitle,
                                    sidebarWidth,
                                    sidebarOpen,
                                    onBack,
                                    backLabel,
                                }}
                                familyCardProps={TREEVIEW_FAMILY_CARD_PROPS}
                                familyListHeader={
                                    <CatalogRootHeader
                                        onProductSelect={product =>
                                            onProductClick(product, filtersRef.current)
                                        }
                                    />
                                }
                                productCardProps={{
                                    onClick: handleClick,
                                    OverrideContent: enableFavoriteAction
                                        ? RowContentWithFavorite
                                        : RowContent,
                                }}
                            />
                        </BoxUnstable>
                    </CatalogFavoritesProvider>
                ) : (
                    <BoxUnstable sx={PRODUCT_GRID_SX}>
                        <ModelSelection
                            treeviewProps={{
                                sidebarTitle,
                                sidebarWidth,
                                sidebarOpen,
                                onBack,
                                backLabel,
                            }}
                            productCardProps={{
                                onClick: handleClick,
                                OverrideContent: enableFavoriteAction
                                    ? CardContentWithFavorite
                                    : CardContent,
                            }}
                        />
                    </BoxUnstable>
                )}
            </CompareProductsProvider>
        </ModelSelectionProvider>
    );
}
